import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { unenrollFromCourse } from "@/lib/unenrollRpc";
import { useAuth } from "@/lib/auth";

export interface Course {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  instructor_id: string | null;
  is_published: boolean;
  status?: "draft" | "preview" | "published";
  created_at: string;
  updated_at: string;
  instructor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  modules_count?: number;
  progress?: number;
  is_enrolled?: boolean;
}

export interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  sequence_order: number;
  is_published: boolean;
  status?: "draft" | "preview" | "published";
  created_at: string;
  updated_at: string;
  is_completed?: boolean;
  is_unlocked?: boolean;
}

export interface ModuleContent {
  id: string;
  module_id: string;
  content_type: "video" | "text" | "image" | "document" | "link" | "audio";
  content_url: string | null;
  content_text: string | null;
  display_title: string | null;
  sequence_order: number;
  created_at: string;
}

interface CourseEnrollmentRecord {
  id: string;
  cohort_id: string | null;
  gating_enforced: boolean;
  enrolled_at: string;
}

async function getPreferredCourseEnrollment(
  courseId: string,
  userId: string
): Promise<CourseEnrollmentRecord | null> {
  const { data, error } = await supabase
    .from("course_enrollments")
    .select("id, cohort_id, gating_enforced, enrolled_at")
    .eq("course_id", courseId)
    .eq("user_id", userId)
    .order("enrolled_at", { ascending: false });

  if (error) throw error;

  const enrollments = (data || []) as CourseEnrollmentRecord[];
  return (
    enrollments.find((enrollment) => enrollment.cohort_id && enrollment.gating_enforced) ||
    enrollments.find((enrollment) => enrollment.cohort_id) ||
    enrollments[0] ||
    null
  );
}

export function useCourses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      const { data: courses, error } = await supabase
        .from("courses")
        .select(`
          *,
          instructor:profiles!courses_instructor_id_fkey(id, full_name, avatar_url)
        `)
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get enrollment status and progress for each course
      if (user) {
        const coursesWithProgress = await Promise.all(
          (courses || []).map(async (course) => {
            const { data: enrollment } = await supabase
              .from("course_enrollments")
              .select("id")
              .eq("course_id", course.id)
              .eq("user_id", user.id)
              .limit(1);

            const { data: modulesCount } = await supabase
              .from("modules")
              .select("id", { count: "exact" })
              .eq("course_id", course.id);

            let progress = 0;
            if (enrollment && enrollment.length > 0) {
              const { data: progressData } = await supabase.rpc("get_course_progress", {
                _user_id: user.id,
                _course_id: course.id,
              });
              progress = progressData || 0;
            }

            return {
              ...course,
              is_enrolled: !!enrollment && enrollment.length > 0,
              modules_count: modulesCount?.length || 0,
              progress,
            };
          })
        );
        return coursesWithProgress as Course[];
      }

      return courses as Course[];
    },
    enabled: true,
  });
}

export function useCourse(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["course", courseId],
    queryFn: async () => {
      const { data: course, error } = await supabase
        .from("courses")
        .select(`
          *,
          instructor:profiles!courses_instructor_id_fkey(id, full_name, avatar_url)
        `)
        .eq("id", courseId)
        .maybeSingle();

      if (error) throw error;
      if (!course) throw new Error("Course not found");

      let enrollment = null;
      let progress = 0;

      if (user) {
        enrollment = await getPreferredCourseEnrollment(courseId, user.id);

        if (enrollment) {
          const { data: progressData } = await supabase.rpc("get_course_progress", {
            _user_id: user.id,
            _course_id: courseId,
          });
          progress = progressData || 0;
        }
      }

      return {
        ...course,
        is_enrolled: !!enrollment,
        progress,
      } as Course;
    },
    enabled: !!courseId,
  });
}

export function useModules(courseId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["modules", courseId],
    queryFn: async () => {
      const { data: modules, error } = await supabase
        .from("modules")
        .select("*")
        .eq("course_id", courseId)
        .order("sequence_order", { ascending: true });

      if (error) throw error;

      if (user) {
        const enrollment = await getPreferredCourseEnrollment(courseId, user.id);

        if (enrollment) {
          const modulesWithProgress = await Promise.all(
            (modules || []).map(async (module) => {
              const { data: progress } = await supabase
                .from("module_progress")
                .select("completed_at")
                .eq("enrollment_id", enrollment.id)
                .eq("module_id", module.id)
                .maybeSingle();

              const { data: isUnlocked } = await supabase.rpc("is_module_unlocked", {
                _user_id: user.id,
                _module_id: module.id,
              });

              return {
                ...module,
                is_completed: !!progress?.completed_at,
                is_unlocked: isUnlocked ?? false,
              };
            })
          );
          return modulesWithProgress as Module[];
        }
      }

      return (modules || []).map((m, idx) => ({
        ...m,
        is_completed: false,
        is_unlocked: idx === 0,
      })) as Module[];
    },
    enabled: !!courseId,
  });
}

export function useModuleContent(moduleId: string) {
  return useQuery({
    queryKey: ["module-content", moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_content")
        .select("*")
        .eq("module_id", moduleId)
        .order("sequence_order", { ascending: true });

      if (error) throw error;
      return data as ModuleContent[];
    },
    enabled: !!moduleId,
  });
}

export function useEnrollCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (courseId: string) => {
      if (!user) throw new Error("Must be logged in to enroll");

      const { data, error } = await supabase
        .from("course_enrollments")
        .upsert(
          { course_id: courseId, user_id: user.id },
          { onConflict: "user_id,course_id", ignoreDuplicates: true }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, courseId) => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["course", courseId] });
      queryClient.invalidateQueries({ queryKey: ["modules", courseId] });
    },
  });
}

export function useCompleteModule() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ moduleId, courseId }: { moduleId: string; courseId: string }) => {
      if (!user) throw new Error("Must be logged in");

      // Server-side gated write. The RPC re-checks enrollment + assignment gating
      // atomically before recording completion, so a learner cannot bypass module
      // gating by hitting the table directly.
      const { data, error } = await supabase.rpc("complete_module", {
        _module_id: moduleId,
      });

      if (error) {
        // Surface the "locked" state the UI already knows how to render.
        if (error.message?.includes("assignments_not_satisfied")) {
          throw new Error(
            "This module is still locked — complete the required assignments before marking it done."
          );
        }
        throw error;
      }
      return data;
    },
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["course", courseId] });
      queryClient.invalidateQueries({ queryKey: ["modules", courseId] });
      queryClient.invalidateQueries({ queryKey: ["module-requirements"] });
      queryClient.invalidateQueries({ queryKey: ["can-progress-from-module"] });
      // Learner-facing curriculum progress: completing a module is exactly when
      // the learner wants to see how far along the whole programme they now are.
      queryClient.invalidateQueries({ queryKey: ["learner-curriculum-progress"] });
      queryClient.invalidateQueries({ queryKey: ["learner-module-progress"] });
    },
  });
}


export interface InstructorCourse extends Course {
  enrolled_count?: number;
  curriculum_id?: string;
}

export function useInstructorCourses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["instructor-courses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get enrollment counts and module counts for each course
      const coursesWithCounts = await Promise.all(
        (data || []).map(async (course) => {
          const { count: enrolledCount } = await supabase
            .from("course_enrollments")
            .select("*", { count: "exact", head: true })
            .eq("course_id", course.id);

          const { count: modulesCount } = await supabase
            .from("modules")
            .select("*", { count: "exact", head: true })
            .eq("course_id", course.id);

          return {
            ...course,
            enrolled_count: enrolledCount || 0,
            modules_count: modulesCount || 0,
          };
        })
      );

      return coursesWithCounts as InstructorCourse[];
    },
    enabled: !!user,
  });
}

export function useCreateCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (course: { title: string; description?: string; curriculum_id?: string; thumbnail_url?: string }) => {
      if (!user) throw new Error("Must be logged in");

      const { data, error } = await supabase
        .from("courses")
        .insert({ ...course, instructor_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-courses"] });
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
    },
  });
}

export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Course> & { id: string }) => {
      const { data, error } = await supabase
        .from("courses")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["instructor-courses"] });
      queryClient.invalidateQueries({ queryKey: ["course", data.id] });
    },
  });
}

export function useCreateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (module: { course_id: string; title: string; description?: string; sequence_order: number }) => {
      const { data, error } = await supabase
        .from("modules")
        .insert(module)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["modules", data.course_id] });
    },
  });
}

export function useUpdateModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, courseId, ...updates }: { id: string; courseId: string; title?: string; description?: string; thumbnail_url?: string | null; status?: "draft" | "preview" | "published"; requires_homework?: boolean }) => {
      const { data, error } = await supabase
        .from("modules")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, courseId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["modules", data.courseId] });
    },
  });
}

export function useToggleModulePublish() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ moduleId, courseId, isPublished }: { moduleId: string; courseId: string; isPublished: boolean }) => {
      const { data, error } = await supabase
        .from("modules")
        .update({ is_published: isPublished })
        .eq("id", moduleId)
        .select()
        .single();

      if (error) throw error;
      return { ...data, courseId };
    },
    onMutate: async ({ moduleId, courseId, isPublished }) => {
      await queryClient.cancelQueries({ queryKey: ["modules", courseId] });
      const previous = queryClient.getQueryData<Module[]>(["modules", courseId]);
      queryClient.setQueryData<Module[]>(["modules", courseId], (old) =>
        old?.map((m) => (m.id === moduleId ? { ...m, is_published: isPublished } : m))
      );
      return { previous, courseId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["modules", context.courseId], context.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ["modules", vars.courseId] });
    },
  });
}

export function useCreateModuleContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: {
      module_id: string;
      content_type: "video" | "text" | "image" | "document" | "link" | "audio";
      content_url?: string;
      content_text?: string;
      display_title?: string;
      sequence_order: number;
    }) => {
      const { data, error } = await supabase
        .from("module_content")
        .insert(content)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["module-content", data.module_id] });
    },
  });
}

export function useUpdateModuleContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, moduleId, ...updates }: { id: string; moduleId: string; display_title?: string; content_url?: string }) => {
      const { data, error } = await supabase
        .from("module_content")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, moduleId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["module-content", data.moduleId] });
    },
  });
}

export function useDeleteModuleContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contentId, moduleId }: { contentId: string; moduleId: string }) => {
      const { error } = await supabase
        .from("module_content")
        .delete()
        .eq("id", contentId);

      if (error) throw error;
      return { moduleId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["module-content", data.moduleId] });
    },
  });
}

export function useDeleteCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courseId: string) => {
      const { error } = await supabase
        .from("courses")
        .delete()
        .eq("id", courseId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-courses"] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
    },
  });
}

export function useDeleteModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ moduleId, courseId }: { moduleId: string; courseId: string }) => {
      const { error } = await supabase
        .from("modules")
        .delete()
        .eq("id", moduleId);

      if (error) throw error;
      return { courseId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["modules", data.courseId] });
    },
  });
}

export function useReorderModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ courseId, modules }: { courseId: string; modules: { id: string; sequence_order: number }[] }) => {
      // Atomic reorder via RPC. The old implementation wrote NEGATIVE sentinel
      // sequence_order values in one loop and final values in a second, with no
      // transaction — a failure between the passes left negative values behind,
      // which breaks is_module_unlocked()'s `sequence_order = 1` fast path.
      const orderedIds = [...modules]
        .sort((a, b) => a.sequence_order - b.sequence_order)
        .map((m) => m.id);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("reorder_modules", {
        _course_id: courseId,
        _module_ids: orderedIds,
      });

      if (error) throw error;

      return { courseId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["modules", data.courseId] });
    },
  });
}

export function useUnenrollCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (courseId: string) => {
      if (!user) throw new Error("Must be logged in to unenroll");

      // Atomic server-side unenroll. Previously this ran the destructive
      // progress deletes client-side FIRST and then deleted the enrollment --
      // but course_enrollments had no DELETE policy, so that last step was
      // filtered out by RLS and returned 200 with 0 rows and error === null.
      // The learner lost all their work and stayed enrolled, with a success
      // toast. The RPC does the wipe and the delete in one transaction and
      // raises if the enrollment delete affects zero rows.
      await unenrollFromCourse(courseId);

      return { courseId };
    },
    onSuccess: (_, courseId) => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["course", courseId] });
      queryClient.invalidateQueries({ queryKey: ["modules", courseId] });
      queryClient.invalidateQueries({ queryKey: ["quiz-attempts"] });
      queryClient.invalidateQueries({ queryKey: ["assignment-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["student_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["homework-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["my-homework-submission"] });
    },
  });
}

export function useCloneCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (courseId: string) => {
      if (!user) throw new Error("Must be logged in");

      // Get the original course
      const { data: original, error: fetchError } = await supabase
        .from("courses")
        .select("*")
        .eq("id", courseId)
        .single();

      if (fetchError || !original) throw new Error("Course not found");

      // Create a clone with "Copy of" prefix
      const { data: clonedCourse, error: cloneError } = await supabase
        .from("courses")
        .insert({
          title: `Copy of ${original.title}`,
          description: original.description,
          thumbnail_url: original.thumbnail_url,
          curriculum_id: original.curriculum_id,
          instructor_id: user.id,
          is_published: false, // Always start as draft
        })
        .select()
        .single();

      if (cloneError) throw cloneError;

      // Clone modules and their content
      const { data: modules } = await supabase
        .from("modules")
        .select("*")
        .eq("course_id", courseId)
        .order("sequence_order", { ascending: true });

      if (modules && modules.length > 0) {
        for (const module of modules) {
          // Create cloned module
          const { data: clonedModule, error: moduleError } = await supabase
            .from("modules")
            .insert({
              course_id: clonedCourse.id,
              title: module.title,
              description: module.description,
              thumbnail_url: module.thumbnail_url,
              sequence_order: module.sequence_order,
            })
            .select()
            .single();

          if (moduleError) continue;

          // Clone module content
          const { data: content } = await supabase
            .from("module_content")
            .select("*")
            .eq("module_id", module.id)
            .order("sequence_order", { ascending: true });

          if (content && content.length > 0) {
            const clonedContent = content.map(c => ({
              module_id: clonedModule.id,
              content_type: c.content_type,
              content_url: c.content_url,
              content_text: c.content_text,
              display_title: c.display_title,
              sequence_order: c.sequence_order,
            }));
            await supabase.from("module_content").insert(clonedContent);
          }
        }
      }

      return clonedCourse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instructor-courses"] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      queryClient.invalidateQueries({ queryKey: ["instructor-curricula"] });
    },
  });
}

export function useCloneModule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ moduleId, courseId }: { moduleId: string; courseId: string }) => {
      // Get the original module
      const { data: original, error: fetchError } = await supabase
        .from("modules")
        .select("*")
        .eq("id", moduleId)
        .single();

      if (fetchError || !original) throw new Error("Module not found");

      // Get current max sequence_order
      const { data: modules } = await supabase
        .from("modules")
        .select("sequence_order")
        .eq("course_id", courseId)
        .order("sequence_order", { ascending: false })
        .limit(1);

      const maxOrder = modules?.[0]?.sequence_order || 0;

      // Create a clone with "Copy of" prefix
      const { data: clonedModule, error: cloneError } = await supabase
        .from("modules")
        .insert({
          course_id: courseId,
          title: `Copy of ${original.title}`,
          description: original.description,
          thumbnail_url: original.thumbnail_url,
          sequence_order: maxOrder + 1,
        })
        .select()
        .single();

      if (cloneError) throw cloneError;

      // Clone module content
      const { data: content } = await supabase
        .from("module_content")
        .select("*")
        .eq("module_id", moduleId)
        .order("sequence_order", { ascending: true });

      if (content && content.length > 0) {
        const clonedContent = content.map(c => ({
          module_id: clonedModule.id,
          content_type: c.content_type,
          content_url: c.content_url,
          content_text: c.content_text,
          display_title: c.display_title,
          sequence_order: c.sequence_order,
        }));
        await supabase.from("module_content").insert(clonedContent);
      }

      return { ...clonedModule, courseId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["modules", data.courseId] });
    },
  });
}
